import { Controller } from 'react-hook-form'
import { Box, Checkbox, Flex, NumberInput, Stack } from '@chakra-ui/react'
import { FormContentBlock } from './FormContentBlock'
import { InfoPopover } from '../InfoPopover/InfoPopover'
import { StyledInput, StyledNumberInputField, StyledSelect, UnderlineError } from './styled'
import { FormLabel } from '../common/FormLabel'

export const InputWrapper = ({ label, type, helperText, nested, fullWidth, children, errors, name, style, isDisabled, required }) => (
    <Box
        width={fullWidth ? '660px' : nested ? '310px' : '320px'}
        display={type === 'boolean' ? 'flex' : 'block'}
        alignItems="center"
        style={style}
    >
        {type === 'boolean' 
            ? (
                <>
                {children}
                <InfoPopover helperText={helperText} />
                </>
            )
            : (
                <>
                <FormLabel helperText={helperText} disabled={isDisabled} required={required}>{label}</FormLabel>
                <Flex justifyContent="space-between">
                    {children}
                </Flex>
                </>
            )
        }
        
        {!!errors[name] &&
            <UnderlineError style={{ marginTop: '8px' }}>
                {errors[name].type === 'required'
                    ? 'Required'
                    : errors[name].message}
            </UnderlineError>
        }
    </Box>
)

const renderField = (props) => {
    const { field, control, errors } = props
    const { name, type, values, render, isDisabled, inputBackground, required, validate } = field
    const customProps = { ...field, errors, nested: props.nested }
    const error = errors[name]
    switch (type) {
        case 'string':
            return (
                <InputWrapper {...customProps}>
                    <Controller
                        name={name}
                        control={control}
                        defaultValue=""
                        rules={{ 
                            required: required,
                            validate: validate
                        }}
                        render={({ field }) => (
                            <StyledInput
                                style={{ font: 'var(--code-font)' }}
                                focusBorderColor={error && 'red'}
                                isInvalid={!!error}
                                isDisabled={isDisabled}
                                {...field}
                                onChange={(e) => {
                                    const value = e.target.value
                                    const trimmedValue = value.trimStart()
                                    field.onChange(trimmedValue)
                                }}
                                onBlur={(e) => {
                                    const value = e.target.value
                                    const trimmedValue = value.trim()
                                    field.onChange(trimmedValue)
                                }}
                            />
                        )}
                    />
                </InputWrapper>
            )
        case 'number':
            return (
                <InputWrapper {...customProps}>
                    <Controller
                        name={name}
                        control={control}
                        rules={{ 
                            required: required,
                            validate: validate
                        }}
                        render={({ field }) => (
                            <NumberInput
                                defaultValue={field.value}
                                width='100%'
                                isDisabled={isDisabled}
                            >
                                <StyledNumberInputField
                                    style={{ font: 'var(--code-font)' }}
                                    focusBorderColor={error && 'red'}
                                    isInvalid={!!error}
                                    {...field}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        field.onChange(Number(value))
                                    }}
                                />
                            </NumberInput>
                        )}
                    />
                </InputWrapper>
            )
        case 'boolean':
            return (
                <InputWrapper {...customProps}>
                    <Controller
                        name={name}
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                            <Checkbox
                                fontWeight='500'
                                isChecked={field.value}
                                isDisabled={isDisabled}
                                {...field}
                            >
                                {props.field.label}
                            </Checkbox>
                        )}
                    />
                </InputWrapper>
            )
        case 'select':
            return (
                <InputWrapper {...customProps}>
                    <Controller
                        name={name}
                        control={control}
                        render={({ field }) => (
                            <StyledSelect
                                style={{ font: 'var(--code-font)' }}
                                background={inputBackground}
                                {...field}
                                isDisabled={props.field.isDisabled}
                            >
                                {values.map((val) => (
                                    <option key={val} value={val}>
                                        {val}
                                    </option>
                                ))}
                            </StyledSelect>
                        )}
                    />
                </InputWrapper>
            )
        case 'custom':
            return render({ ...props })
        case 'section':
            return (
                <FormContentBlock
                    header={field.name}
                    half={field.half}
                    nested={props.nested}
                    style={field.style}
                >
                    {renderSection({ ...props, nested: true, section: field })}
                </FormContentBlock>
            )
        case 'container':
            return <Flex>{renderSection({ ...props, section: field })}</Flex>
        default:
            return <div />
    }
}

const renderSection = (props) => {
    // console.log('render section', props)
    const { section } = props
    return section.rows.map((row) => {
        const content = () => {
            switch (row.type) {
                case 'line':
                    return (
                        <Flex
                            mt={3}
                            gridColumn="1 / 3"
                            mb={4}
                            justifyContent={row.justify ? 'space-between' : 'space-around'}
                            {...row.style}
                        >
                            {row.content.map((el) => {
                                return renderField({ ...props, field: el })
                            })}
                        </Flex>
                    )
                case 'column':
                    return (
                        <Stack
                            justifyContent='space-around'
                            spacing={props.nested ? 2 : 4}
                            style={row.style}
                        >
                            {row.content.map((el) => {
                                return renderField({ ...props, field: el })
                            })}
                        </Stack>
                    )
                case 'section':
                    return (
                        <FormContentBlock
                            disabled={row.content.disabled}
                            header={row.content.name}
                            half={row.content.half}
                            nested={true}
                        >
                            {renderSection({ ...props, nested: true, section: row.content })}
                        </FormContentBlock>
                    )
                case 'full-width':
                    return (
                        <Flex mt={4} pb={3}>
                            {renderField({ ...props, field: row.content })}
                        </Flex>
                    )
                default:
                    return <></>
            }
        }

        return content()
    })
}

export const renderAddSessionForm = ({ schema, ...props }) => {
    return schema.map((block) => {
        if (block.type === 'section')
            return (
                <FormContentBlock header={block.name} half={block.half}>
                    {renderSection({ section: block, ...props })}
                </FormContentBlock>
            )
        else return renderSection({ section: block, ...props })
    })
}
